import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { SEO } from '../../utils/seo';

export default function MartynsLawPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Martyn's Law - Protect Duty Legislation",
    "description": "Comprehensive guide to Martyn's Law (Protect Duty) - new UK counter-terrorism legislation requiring venues and events to implement protective security measures.",
    "author": {
      "@type": "Organization",
      "name": "JL Security"
    },
    "publisher": {
      "@type": "Organization",
      "name": "JL Security",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "North Wales",
        "addressRegion": "Wales",
        "addressCountry": "GB"
      }
    },
    "about": {
      "@type": "Thing",
      "name": "Martyn's Law",
      "description": "UK counter-terrorism legislation requiring protective security measures at public venues"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is Martyn's Law?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Martyn's Law, officially known as the Protect Duty, is new UK legislation requiring qualifying venues and events to implement protective security measures against terrorism. Named after Martyn Hett, who was killed in the 2017 Manchester Arena attack, the law aims to enhance public safety at publicly accessible locations."
        }
      },
      {
        "@type": "Question",
        "name": "Who does Martyn's Law apply to?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Martyn's Law applies to qualifying premises and events with capacity thresholds. Standard Tier applies to venues with capacity of 100-799 people, requiring basic procedural measures. Enhanced Tier applies to venues with capacity of 800+ people, requiring comprehensive security assessments, planning, and training."
        }
      },
      {
        "@type": "Question",
        "name": "When does Martyn's Law come into force?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The legislation is expected to come into force in 2024-2025, with implementation periods allowing organisations time to comply. Organisations should begin preparation now to ensure readiness when the law takes effect."
        }
      },
      {
        "@type": "Question",
        "name": "What are the requirements under Martyn's Law?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Requirements vary by tier. Standard Tier requires terrorism risk assessments, protective security procedures, staff training and awareness, and incident response planning. Enhanced Tier requires all Standard Tier measures plus designated security personnel, comprehensive security systems, regular exercises and testing, and detailed security documentation."
        }
      }
    ]
  };

  return (
    <>
      <SEO
        title="Martyn's Law North Wales - Protect Duty Compliance - JL Security"
        description="Comprehensive guide to Martyn's Law (Protect Duty) - new UK counter-terrorism legislation requiring venues and events to implement protective security measures. Expert compliance support from JL Security."
        keywords="Martyn's Law, Protect Duty, counter-terrorism, venue security, event security, terrorism risk assessment, public safety legislation"
        schema={[schema, faqSchema]}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-red-900 via-gray-900 to-gray-800">
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <div className="w-20 h-20 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-5xl text-white"></i>
              </div>
              <h1 className="text-5xl sm:text-6xl font-bold mb-6 font-poppins">Martyn's Law</h1>
              <p className="text-xl text-gray-200 max-w-3xl mx-auto font-inter">
                Understanding the Protect Duty and preparing your organisation for new counter-terrorism legislation
              </p>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6 font-poppins">What is Martyn's Law?</h2>
                <p className="text-gray-600 mb-6 leading-relaxed font-inter">
                  Martyn's Law, officially known as the <strong>Protect Duty</strong>, is new UK legislation requiring qualifying venues and events to implement protective security measures against terrorism. Named after Martyn Hett, who was killed in the 2017 Manchester Arena attack, the law aims to enhance public safety at publicly accessible locations.
                </p>
                <p className="text-gray-600 mb-6 leading-relaxed font-inter">
                  The legislation introduces a legal requirement for those responsible for certain premises and events to consider terrorist threats and take appropriate steps to protect the public. This represents a significant shift in how organisations must approach security planning and preparedness.
                </p>
                <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-r-lg">
                  <p className="text-sm text-gray-700 font-inter">
                    <strong className="text-red-900">Expected Implementation:</strong> The legislation is expected to come into force in 2024-2025, with implementation periods allowing organisations time to comply.
                  </p>
                </div>
              </div>
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/81804a6512e131e9680d8593c9b95da9.jpg"
                  alt="Martyn's Law Venue Security"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Who Does It Apply To */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Who Does Martyn's Law Apply To?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-building-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Standard Tier</h3>
                  <p className="text-sm text-gray-600 mb-4 font-inter">Capacity: 100-799 people</p>
                  <p className="text-gray-600 mb-4 font-inter">
                    Venues and events with capacity between 100 and 799 people will need to implement basic procedural measures focused on preparedness and awareness.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Simple terrorism risk assessments</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Basic protective security procedures</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Staff awareness training</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Incident response planning</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-red-50 to-white rounded-xl p-8 shadow-lg border border-red-100">
                  <div className="w-14 h-14 bg-red-600 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-building-4-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Enhanced Tier</h3>
                  <p className="text-sm text-gray-600 mb-4 font-inter">Capacity: 800+ people</p>
                  <p className="text-gray-600 mb-4 font-inter">
                    Larger venues and events with capacity of 800 or more people will need to implement more comprehensive security measures and demonstrate ongoing compliance.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Comprehensive security risk assessments</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Designated security personnel</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Security systems and technology</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Regular exercises and testing</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Qualifying Premises */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Qualifying Premises and Events</h2>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <p className="text-gray-600 mb-6 font-inter">
                  Martyn's Law applies to a wide range of publicly accessible locations and events. If your organisation operates or manages any of the following, you may need to comply:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="flex items-start gap-3">
                    <i className="ri-music-line text-2xl text-red-600 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1 font-poppins">Entertainment Venues</h4>
                      <p className="text-sm text-gray-600 font-inter">Theatres, concert halls, cinemas, nightclubs</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-shopping-bag-line text-2xl text-red-600 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1 font-poppins">Retail & Hospitality</h4>
                      <p className="text-sm text-gray-600 font-inter">Shopping centres, restaurants, bars, hotels</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-football-line text-2xl text-red-600 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1 font-poppins">Sports & Recreation</h4>
                      <p className="text-sm text-gray-600 font-inter">Stadiums, leisure centres, gyms, sports facilities</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-government-line text-2xl text-red-600 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1 font-poppins">Public Buildings</h4>
                      <p className="text-sm text-gray-600 font-inter">Museums, galleries, libraries, community centres</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-hospital-line text-2xl text-red-600 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1 font-poppins">Healthcare & Education</h4>
                      <p className="text-sm text-gray-600 font-inter">Hospitals, universities, colleges, schools</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-calendar-event-line text-2xl text-red-600 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1 font-poppins">Events & Festivals</h4>
                      <p className="text-sm text-gray-600 font-inter">Outdoor events, festivals, conferences, exhibitions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Requirements */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Key Requirements Under Martyn's Law</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-file-list-3-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Risk Assessment</h3>
                  <p className="text-gray-600 font-inter">
                    Conduct terrorism-specific risk assessments considering your venue's characteristics, location, and potential vulnerabilities. Assessments must be documented, regularly reviewed, and inform your protective security measures.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-shield-check-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Protective Measures</h3>
                  <p className="text-gray-600 font-inter">
                    Implement proportionate security measures based on your risk assessment. This may include access control, surveillance systems, physical barriers, security personnel, and visitor screening procedures.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-team-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Staff Training</h3>
                  <p className="text-gray-600 font-inter">
                    Ensure all staff receive appropriate training on terrorism awareness, recognising suspicious behaviour, emergency procedures, and their specific roles in security incidents. Training must be regular and documented.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-alarm-warning-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Incident Response</h3>
                  <p className="text-gray-600 font-inter">
                    Develop and maintain clear procedures for responding to security incidents, including evacuation plans, lockdown procedures, communication protocols, and coordination with emergency services.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-file-text-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Documentation</h3>
                  <p className="text-gray-600 font-inter">
                    Maintain comprehensive records of risk assessments, security measures, training activities, exercises, and incidents. Documentation demonstrates compliance and supports continuous improvement.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-refresh-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Regular Review</h3>
                  <p className="text-gray-600 font-inter">
                    Continuously review and update your security arrangements in response to changing threats, incidents, lessons learned, and operational changes. Enhanced tier premises must conduct regular exercises.
                  </p>
                </div>
              </div>
            </div>

            {/* Preparing for Compliance */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Preparing for Compliance</h2>
              <div className="bg-gradient-to-br from-red-50 to-gray-50 rounded-2xl p-12">
                <div className="max-w-4xl mx-auto">
                  <p className="text-gray-600 mb-8 text-center font-inter">
                    Organisations should begin preparation now to ensure readiness when Martyn's Law comes into force. Follow these steps to prepare:
                  </p>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4 bg-white rounded-lg p-6 shadow-md">
                      <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold font-poppins">1</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 font-poppins">Determine Your Tier</h4>
                        <p className="text-sm text-gray-600 font-inter">Calculate your venue or event capacity to understand whether you fall under Standard or Enhanced tier requirements.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 bg-white rounded-lg p-6 shadow-md">
                      <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold font-poppins">2</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 font-poppins">Conduct Gap Analysis</h4>
                        <p className="text-sm text-gray-600 font-inter">Review your current security arrangements against Martyn's Law requirements to identify gaps and areas for improvement.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 bg-white rounded-lg p-6 shadow-md">
                      <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold font-poppins">3</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 font-poppins">Develop Action Plan</h4>
                        <p className="text-sm text-gray-600 font-inter">Create a prioritised plan to address identified gaps, including timelines, responsibilities, and resource requirements.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 bg-white rounded-lg p-6 shadow-md">
                      <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold font-poppins">4</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 font-poppins">Implement Measures</h4>
                        <p className="text-sm text-gray-600 font-inter">Put in place necessary security measures, procedures, and training programmes to meet your tier requirements.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 bg-white rounded-lg p-6 shadow-md">
                      <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold font-poppins">5</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 font-poppins">Document Everything</h4>
                        <p className="text-sm text-gray-600 font-inter">Maintain comprehensive records of all assessments, measures, training, and reviews to demonstrate compliance.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 bg-white rounded-lg p-6 shadow-md">
                      <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold font-poppins">6</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 font-poppins">Test and Review</h4>
                        <p className="text-sm text-gray-600 font-inter">Regularly test your procedures through exercises and drills, and continuously review and improve your security arrangements.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Frequently Asked Questions</h2>
              <div className="max-w-4xl mx-auto space-y-6">
                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <h4 className="text-lg font-bold text-gray-900 mb-3 font-poppins">What penalties apply for non-compliance?</h4>
                  <p className="text-gray-600 font-inter">
                    While final enforcement details are being confirmed, non-compliance with Martyn's Law is expected to result in significant penalties including fines, enforcement notices, and potential criminal sanctions for serious breaches. The regulator will have powers to inspect premises and investigate compliance.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <h4 className="text-lg font-bold text-gray-900 mb-3 font-poppins">Do small venues need to comply?</h4>
                  <p className="text-gray-600 font-inter">
                    Venues with capacity below 100 people are not currently expected to fall within scope of the legislation. However, all organisations should consider terrorism risk as part of their general security and health and safety obligations.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <h4 className="text-lg font-bold text-gray-900 mb-3 font-poppins">How is capacity calculated?</h4>
                  <p className="text-gray-600 font-inter">
                    Capacity is typically based on the maximum number of people who can be present at any one time, considering factors such as fire safety capacity, ticketed capacity, or operational capacity. Guidance will be provided on capacity calculation methods.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <h4 className="text-lg font-bold text-gray-900 mb-3 font-poppins">Can existing security measures count towards compliance?</h4>
                  <p className="text-gray-600 font-inter">
                    Yes, existing security measures that address terrorism risk can count towards Martyn's Law compliance. However, you must ensure they are specifically documented and assessed in relation to terrorism threats, and that they meet the required standards.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <h4 className="text-lg font-bold text-gray-900 mb-3 font-poppins">What about temporary or seasonal venues?</h4>
                  <p className="text-gray-600 font-inter">
                    Temporary venues and events that meet the capacity thresholds will need to comply with Martyn's Law. This includes seasonal attractions, pop-up venues, and temporary event structures. The requirements apply whenever the venue or event is operational.
                  </p>
                </div>
              </div>
            </div>

            {/* How JL Security Helps */}
            <div className="bg-gray-900 rounded-2xl p-12 mb-16">
              <h2 className="text-3xl font-bold text-white mb-6 text-center font-poppins">How JL Security Helps</h2>
              <p className="text-gray-300 mb-8 text-center max-w-3xl mx-auto font-inter">
                JL Security supports organisations by translating Martyn's Law into practical, proportionate actions aligned to real operations. Support includes:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                <div className="bg-gray-800 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <i className="ri-search-line text-2xl text-red-600 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-white mb-2 font-poppins">Readiness Reviews & Gap Analysis</h4>
                      <p className="text-sm text-gray-300 font-inter">Comprehensive assessment of your current security posture against Martyn's Law requirements, identifying gaps and priorities.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-800 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <i className="ri-shield-check-line text-2xl text-red-600 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-white mb-2 font-poppins">Terrorism Risk Assessments</h4>
                      <p className="text-sm text-gray-300 font-inter">Expert terrorism-specific risk assessments and practical action planning tailored to your venue and operations.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-800 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <i className="ri-graduation-cap-line text-2xl text-red-600 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-white mb-2 font-poppins">Staff Awareness & Training</h4>
                      <p className="text-sm text-gray-300 font-inter">Role-specific training programmes delivered online or through blended learning, ensuring all staff understand their responsibilities.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-800 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <i className="ri-file-list-3-line text-2xl text-red-600 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-white mb-2 font-poppins">Compliance Integration</h4>
                      <p className="text-sm text-gray-300 font-inter">Integration with existing compliance and emergency frameworks, avoiding duplication and ensuring efficient implementation.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-800 rounded-lg p-6 md:col-span-2">
                  <div className="flex items-start gap-4">
                    <i className="ri-calendar-event-line text-2xl text-red-600 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-white mb-2 font-poppins">Event & Peak Period Support</h4>
                      <p className="text-sm text-gray-300 font-inter">Support for higher-risk events or peak periods where additional controls may be appropriate, including temporary security enhancements and operational advice.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center bg-gradient-to-br from-red-50 to-gray-50 rounded-2xl p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 font-poppins">Prepare for Martyn's Law</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto font-inter">
                Don't wait until the legislation comes into force. Contact us today to assess your readiness and develop a practical compliance plan
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/#contact" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  Get Readiness Review
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
                <a 
                  href="tel:07834561015" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call 07834 561015
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
